import { NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { SolButtonType } from '../../../models/core/sol-button';
import {
  SolContent,
  SolContentAnchorLinkTarget,
  SolContentBlock,
  SolContentCodeBlock,
  SolContentDividerBlock,
  SolContentHeadingBlock,
  SolContentImageBlock,
  SolContentImageInline,
  SolContentInline,
  SolContentLinkClickEvent,
  SolContentLinkInline,
  SolContentLinkTarget,
  SolContentListBlock,
  SolContentListItem,
  SolContentParagraphBlock,
  SolContentQuoteBlock,
  SolContentTableBlock,
} from '../../../models/core/sol-content';
import { NavigationTarget, RouterTools } from '../../../tools/router-tools';
import { AbstractComponent } from '../../abstract.component';
import { SolButtonComponent } from '../sol-button/sol-button.component';
import { SolImageComponent } from '../sol-image/sol-image.component';
import { NavigationService } from '../../../services/navigation.service';
import { SolReportTableComponent } from '../../overview/sol-report-table/sol-report-table.component';
import { SolContentComponentState } from './sol-content.component.state';

@Component({
  selector: 'sol-content',
  standalone: true,
  imports: [NgTemplateOutlet, SolButtonComponent, SolImageComponent, SolReportTableComponent],
  templateUrl: './sol-content.component.html',
  host: { class: 'sol-content-host' },
})
export class SolContentComponent extends AbstractComponent<SolContentComponentState> {
  protected readonly SolButtonType = SolButtonType;

  // Dependencies
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly navigationService = inject(NavigationService);

  // Computed
  protected readonly blocks$ = this.compute(state => state.content.blocks);
  protected readonly tablesByBlockId$ = this.compute(state => state.tablesByBlockId);

  // Events
  @Output('linkClick')
  private readonly linkClickEmitter = new EventEmitter<SolContentLinkClickEvent>();

  constructor() {
    super(SolContentComponentState.of());
  }

  @Input()
  public set content(content: SolContent | undefined) {
    if (content !== undefined) {
      this.update(state => state.withContent(content));
    }
  }

  @Input()
  public set linkTarget(linkTarget: NavigationTarget) {
    this.update(state => state.withLinkTarget(linkTarget));
  }

  protected onLinkClick(link: SolContentLinkInline, event: MouseEvent): void {
    this.followLink(link.target, this.toNavigationTarget(event));
  }

  protected onLinkAuxClick(link: SolContentLinkInline): void {
    this.followLink(link.target, NavigationTarget.NewTab);
  }

  protected headingClass(block: SolContentHeadingBlock): string {
    switch (block.level) {
      case 1:
        return 'sol-text-style-h1';
      case 2:
        return 'sol-text-style-h2';
      case 3:
        return 'sol-text-style-h3';
      case 4:
        return 'sol-text-style-h4';
      case 5:
      case 6:
        return 'sol-text-style-body-bold';
    }
  }

  protected asHeading(block: SolContentBlock): SolContentHeadingBlock {
    return block as SolContentHeadingBlock;
  }

  protected asParagraph(block: SolContentBlock): SolContentParagraphBlock {
    return block as SolContentParagraphBlock;
  }

  protected asQuote(block: SolContentBlock): SolContentQuoteBlock {
    return block as SolContentQuoteBlock;
  }

  protected asList(block: SolContentBlock): SolContentListBlock {
    return block as SolContentListBlock;
  }

  protected asTable(block: SolContentBlock): SolContentTableBlock {
    return block as SolContentTableBlock;
  }

  protected asCodeBlock(block: SolContentBlock): SolContentCodeBlock {
    return block as SolContentCodeBlock;
  }

  protected asImageBlock(block: SolContentBlock): SolContentImageBlock {
    return block as SolContentImageBlock;
  }

  protected asDivider(block: SolContentBlock): SolContentDividerBlock {
    return block as SolContentDividerBlock;
  }

  protected asLink(segment: SolContentInline): SolContentLinkInline {
    return segment as SolContentLinkInline;
  }

  protected asImageInline(segment: SolContentInline): SolContentImageInline {
    return segment as SolContentImageInline;
  }

  protected asListItem(item: SolContentListItem): SolContentListItem {
    return item;
  }

  private followLink(target: SolContentLinkTarget, navigationTarget: NavigationTarget): void {
    if (target.type === 'anchor') {
      this.navigateToAnchor(target);
      return;
    }
    if (this.linkClickEmitter.observed) {
      this.linkClickEmitter.emit({ target, navigationTarget });
      return;
    }
    switch (target.type) {
      case 'route':
        this.navigationService.navigate(target.routeId, {
          fragment: target.fragment,
          target: navigationTarget,
        });
        return;
      case 'url':
        this.navigationService.navigateByUrl(target.url, { target: navigationTarget });
        return;
    }
  }

  private navigateToAnchor(target: SolContentAnchorLinkTarget): void {
    const hostElement = this.host.nativeElement as HTMLElement;
    const element = hostElement.querySelector(`#${target.anchorId}`) ?? document.getElementById(target.anchorId);
    element?.scrollIntoView({ block: 'start' });
  }

  private toNavigationTarget(event: MouseEvent): NavigationTarget {
    if (event.ctrlKey || event.metaKey || event.shiftKey) {
      return NavigationTarget.NewTab;
    }
    return this.state.linkTarget;
  }
}
